import { useReduxState } from 'hooks';
import { LoadingIndicator, Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';
import { StatCount } from 'components/common/stats';
import { BoxProvider } from './BoxContext';

import type { BoxAction } from 'components/common/box/BoxContext';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  children?: React.ReactNode;
  disabledOverlayContent?: boolean | string;
  disabledOverlayIcon?: IconProp;
  getDispatch?: (dispatch: React.Dispatch<BoxAction>) => void;
  headerContent?: string | React.ReactNode;
  headerContentWrap?: boolean;
  headerStyle?: React.CSSProperties;
  loading?: boolean;
  padding?: number | string;
  style?: React.CSSProperties;
  subHeaderContent?: string | React.ReactNode;
  subHeaderStyle?: React.CSSProperties;
  title?: string | React.ReactNode;
  titleCount?: number;
  titleDescription?: string | React.ReactNode;
  titleTooltip?: string;
}

const Box = ({
  children,
  disabledOverlayContent,
  disabledOverlayIcon,
  getDispatch,
  headerContent,
  headerContentWrap,
  headerStyle,
  loading,
  padding,
  style,
  subHeaderContent,
  subHeaderStyle,
  title,
  titleCount,
  titleDescription,
  titleTooltip
}: Props) => {
  const { theme } = useReduxState();

  return (
    <BoxProvider getDispatch={getDispatch}>
      <div className="box" style={style}>
        {disabledOverlayContent ? (
          <div className="disabled-overlay">
            {disabledOverlayIcon ? (
              <div style={{ marginBottom: 5 }}>
                <Icon icon={disabledOverlayIcon} />
              </div>
            ) : null}
            {disabledOverlayContent}
          </div>
        ) : null}

        {title || headerContent ? (
          <div className="box-header" style={headerStyle}>
            {title ? (
              <div className="box-header-title">
                <div className="box-header-title-content">
                  {titleTooltip ? (
                    <Tooltip content={titleTooltip}>
                      {title}
                      <Icon
                        icon="question-circle"
                        style={{
                          fontSize: '1.2rem',
                          marginLeft: 5,
                          marginTop: 6
                        }}
                      />
                    </Tooltip>
                  ) : (
                    title
                  )}

                  {titleCount ? (
                    <StatCount
                      isLarge={true}
                      style={{ marginLeft: 7 }}
                      value={titleCount}
                    />
                  ) : null}
                </div>

                {titleDescription ? (
                  <div className="box-header-title-description">
                    {titleDescription}
                  </div>
                ) : null}
              </div>
            ) : null}

            {headerContent ? (
              <div className="box-header-content">{headerContent}</div>
            ) : null}
          </div>
        ) : null}

        {subHeaderContent ? (
          <div className="box-subheader-content" style={subHeaderStyle}>
            {subHeaderContent}
          </div>
        ) : null}

        {loading || children ? (
          <div
            className="box-content"
            style={{
              padding: padding !== undefined ? padding : 15
            }}
          >
            {loading ? <LoadingIndicator /> : children}
          </div>
        ) : null}

        <style jsx>{`
          .box {
            background-color: white;
            border-radius: ${theme.cardBorderRadius};
            box-shadow: ${theme.cardBoxShadow};
            display: flex;
            flex-direction: column;
            position: relative;
          }
          .box-header {
            align-items: center;
            background-color: white;
            border-bottom: ${subHeaderContent || loading || children
              ? `1px solid ${theme.lightGreyDark}`
              : 'none'};
            border-radius: ${subHeaderContent || loading || children
              ? `${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`
              : theme.cardBorderRadius};
            color: ${theme.textColorLight};
            display: flex;
            flex-wrap: ${headerContentWrap ? 'wrap' : 'nowrap'};
            font-size: 1.6rem;
            font-weight: 700;
            padding: 0 15px 10px;
            position: relative;
          }
          .box-header-title {
            flex-grow: 1;
            flex-shrink: 1;
            line-height: 1.4;
            margin-top: 10px;
            padding: 4px 0;
          }
          .box-header-title-content {
            display: flex;
          }
          .box-header-title-description {
            color: ${theme.textColorLight};
            font-size: 1.3rem;
            font-weight: 400;
            margin-top: 2px;
            max-width: 540px;
          }
          .box-header-content {
            flex-grow: ${title ? '0' : '1'};
            flex-shrink: 1;
            margin-top: 10px;
          }
          .box-subheader-content {
            align-items: center;
            background-color: white;
            border-bottom: 1px solid ${theme.lightGreyDark};
            color: ${theme.textColorLight};
            display: flex;
            flex-wrap: wrap;
            padding: 10px 15px;
            position: relative;
          }
          .box-content {
            background-color: white;
            border-radius: ${title
              ? `0 0 ${theme.cardBorderRadius} ${theme.cardBorderRadius}`
              : theme.cardBorderRadius};
            flex-grow: 1;
            flex-shrink: 1;
          }
          .disabled-overlay {
            align-items: center;
            background-color: rgba(255, 255, 255, 0.8);
            border-radius: ${theme.cardBorderRadius};
            color: ${theme.textColorLighter};
            display: flex;
            flex-direction: column;
            font-size: 1.5rem;
            font-weight: 600;
            justify-content: center;
            padding: 15px;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 1;
          }
        `}</style>
      </div>
    </BoxProvider>
  );
};

export default Box;
